import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Tabs, Tab, Chip, Button, Menu, MenuItem, Dialog, DialogTitle, DialogContent, IconButton, TextField, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CodeIcon from "@mui/icons-material/Code";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
} from "recharts";
import { getReportingPortfolio, createEmbedToken } from "./api/underwritingApi";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import {
  C, bandColor, bandLabel, toNum, fmtUSD, fmtFull, regionOf, exportCSV, exportExcel, exportPDF,
} from "./reporting/reportingUtils";
import DrillDownDialog from "./reporting/DrillDownDialog";
import ReportBuilder from "./reporting/ReportBuilder";
import AlertsPanel, { evaluateRules, loadRules } from "./reporting/AlertsPanel";
import { buildNarrative, detectAnomalies, forecastSeries } from "./reporting/insights";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SchedulesPanel from "./reporting/SchedulesPanel";
import BIReportingTrial from "./reporting/BIReportingTrial";
import { BI_FEATURES } from "./reporting/BIWalkthrough";
import SpotlightTour from "./reporting/SpotlightTour";
import { readTrial, isTrialActive } from "./reporting/biTrial";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { JFROG_TENANT_ID } from "./config/tenantRollouts";
import BrandThinkingLoader from "./components/BrandThinkingLoader";

const KpiCard = ({ label, value, sub, accent, onClick }) => (
  <Box onClick={onClick} sx={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px", p: "16px 18px",
    display: "flex", flexDirection: "column", gap: "4px", minWidth: 0, cursor: onClick ? "pointer" : "default",
    transition: "border-color .15s", "&:hover": onClick ? { borderColor: C.blue } : {} }}>
    <Typography sx={{ fontSize: 11.5, letterSpacing: ".06em", textTransform: "uppercase", color: C.muted, fontWeight: 700 }}>{label}</Typography>
    <Typography sx={{ fontSize: 26, fontWeight: 700, color: accent || C.ink, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: 12.5, color: C.muted }}>{sub}</Typography>}
  </Box>
);

const ChartCard = ({ title, subtitle, children, right }) => (
  <Box sx={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px", p: "18px 20px" }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 12.5, color: C.muted, mt: "2px" }}>{subtitle}</Typography>}
      </Box>
      {right}
    </Box>
    {children}
  </Box>
);

const ActionCard = ({ eyebrow, value, detail, tone, action, onClick }) => (
  <Box component="button" type="button" onClick={onClick} sx={{
    appearance: "none", textAlign: "left", width: "100%", border: `1px solid ${C.line}`,
    borderRadius: "14px", background: "#fff", p: "15px 16px", cursor: "pointer",
    fontFamily: "inherit", display: "flex", flexDirection: "column", minWidth: 0,
    transition: "transform .16s ease, border-color .16s ease, box-shadow .16s ease",
    "&:hover": { transform: "translateY(-1px)", borderColor: tone, boxShadow: "0 10px 24px rgba(28,45,80,.08)" },
    "&:focus-visible": { outline: `3px solid ${tone}30`, outlineOffset: 2 },
  }}>
    <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 800, letterSpacing: ".075em", textTransform: "uppercase" }}>{eyebrow}</Typography>
    <Typography sx={{ color: tone, fontSize: 24, lineHeight: 1.15, fontWeight: 800, mt: .6, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    <Typography sx={{ color: C.slate, fontSize: 12.5, lineHeight: 1.4, mt: .5, minHeight: 35 }}>{detail}</Typography>
    <Box sx={{ color: C.blue, display: "flex", alignItems: "center", gap: .5, fontSize: 12, fontWeight: 700, mt: 1 }}>
      {action}<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />
    </Box>
  </Box>
);

const axis = { fontSize: 11, fill: C.muted };
const gridProps = { strokeDasharray: "3 3", stroke: "#EEF1F7", vertical: false };

const BIReporting = () => {
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const currentTenant = useSelector((state) => state.userInfo.tenantId);
  const URLTenant = getTenantIdFromURL();
  const tenantId = isAdmin ? URLTenant : currentTenant;
  const isJfrogTenant = tenantId === JFROG_TENANT_ID;
  const navigate = useNavigate();

  // Open a counterparty's report (company page). BI is admin-only, so use the tenant path.
  const openCompany = (companyId) => {
    if (!companyId) return;
    navigate(isAdmin ? `/tenant-dashboard/${URLTenant}/company/${companyId}` : `/company/${companyId}`);
  };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [drill, setDrill] = useState({ open: false, title: "", rows: [] });
  const [exportEl, setExportEl] = useState(null);
  const [toolsEl, setToolsEl] = useState(null);
  const [alertRules, setAlertRules] = useState(() => loadRules(tenantId));
  const [embedCode, setEmbedCode] = useState(null);
  const [embedBusy, setEmbedBusy] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  // Trial gate: dashboards below are locked until the 30-day trial is accepted.
  const [trial, setTrial] = useState(() => readTrial(tenantId));
  useEffect(() => { setTrial(readTrial(tenantId)); }, [tenantId]);

  // WalkMe-style tour over the real tabs on the page.
  const tourSteps = (isJfrogTenant ? BI_FEATURES.slice(0, 5) : BI_FEATURES).map((f, idx) => ({
    selector: `[data-tour="bi-tab-${idx}"]`,
    title: f.label,
    text: f.blurb,
    onEnter: () => setTab(idx),
  }));

  const getEmbed = async () => {
    setEmbedBusy(true);
    try {
      const r = await createEmbedToken(tenantId);
      setEmbedCode(`<iframe src="${window.location.origin}/embed/${r.token}" width="480" height="410" style="border:1px solid #e6ebf3;border-radius:12px" title="CrediArc portfolio summary"></iframe>`);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Couldn't create an embed link.");
    } finally { setEmbedBusy(false); }
  };

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      // Server-side reporting read path (admin-enforced, slim payload) instead of shipping the
      // full raw summary docs to the browser.
      const { rows: raw } = await getReportingPortfolio(tenantId);
      const mapped = (raw || []).map((r) => ({
        ...r,
        score: r.score != null ? Number(r.score) : null,
        recLimit: toNum(r.recLimit),
        appLimit: toNum(r.appLimit),
        region: regionOf(r.country),
        createdAt: r.createdAt ? new Date(r.createdAt) : null,
      }));
      setRows(mapped);
    } catch (e) {
      console.error("[BIReporting] load failed:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const openDrill = (title, subset) => setDrill({ open: true, title, rows: subset });

  const m = useMemo(() => {
    const n = rows.length;
    const scored = rows.filter((r) => r.score != null);
    const avgScore = scored.length ? scored.reduce((a, r) => a + r.score, 0) / scored.length : null;
    const totalRec = rows.reduce((a, r) => a + r.recLimit, 0);
    const totalApp = rows.reduce((a, r) => a + r.appLimit, 0);
    const approvedRows = rows.filter((r) => String(r.status).toUpperCase() === "APPROVED");
    const pendingRows = rows.filter((r) => String(r.status).toUpperCase() === "PENDING");

    const hist = Array.from({ length: 10 }, (_, i) => ({ bucket: `${i + 1}`, count: 0, _rows: [] }));
    scored.forEach((r) => { const b = Math.min(9, Math.max(0, Math.round(r.score) - 1)); hist[b].count += 1; hist[b]._rows.push(r); });

    const bandRows = { "Low (1–3.5)": [], "Moderate (3.6–6.5)": [], "High (6.6–10)": [], "Unscored": [] };
    rows.forEach((r) => bandRows[bandLabel(r.score)].push(r));
    const bands = [
      { key: "Low (1–3.5)", color: C.green }, { key: "Moderate (3.6–6.5)", color: C.amber },
      { key: "High (6.6–10)", color: C.red }, { key: "Unscored", color: C.muted },
    ].map((b) => ({ ...b, count: bandRows[b.key].length, _rows: bandRows[b.key] }));

    const statusMap = {};
    rows.forEach((r) => { const k = String(r.status || "PENDING").toUpperCase(); (statusMap[k] = statusMap[k] || []).push(r); });
    const statusData = Object.entries(statusMap).map(([k, arr]) => ({ name: k.charAt(0) + k.slice(1).toLowerCase(), value: arr.length, _rows: arr }));

    const bySectorMap = {};
    rows.forEach((r) => { const k = r.sector || "Unspecified"; (bySectorMap[k] = bySectorMap[k] || { sector: k, _rows: [], exposure: 0, scoreSum: 0, scoreN: 0 }); bySectorMap[k]._rows.push(r); bySectorMap[k].exposure += r.recLimit; if (r.score != null) { bySectorMap[k].scoreSum += r.score; bySectorMap[k].scoreN += 1; } });
    const bySector = Object.values(bySectorMap).map((s) => ({ ...s, count: s._rows.length, avgScore: s.scoreN ? +(s.scoreSum / s.scoreN).toFixed(1) : null })).sort((a, b) => b.exposure - a.exposure).slice(0, 10);

    const byRegionMap = {};
    rows.forEach((r) => { const k = r.region || "Other"; (byRegionMap[k] = byRegionMap[k] || { region: k, _rows: [], exposure: 0 }); byRegionMap[k]._rows.push(r); byRegionMap[k].exposure += r.recLimit; });
    const byRegion = Object.values(byRegionMap).map((r) => ({ ...r, count: r._rows.length })).sort((a, b) => b.exposure - a.exposure);

    const byMonthMap = {};
    rows.forEach((r) => { if (!r.createdAt || isNaN(r.createdAt)) return; const k = `${r.createdAt.getFullYear()}-${String(r.createdAt.getMonth() + 1).padStart(2, "0")}`; byMonthMap[k] = (byMonthMap[k] || 0) + 1; });
    const overTime = Object.entries(byMonthMap).sort(([a], [b]) => a.localeCompare(b)).slice(-12).map(([k, v]) => ({ month: k, count: v }));

    const topExposure = [...rows].sort((a, b) => b.recLimit - a.recLimit).slice(0, 8);
    const topRisk = scored.filter((r) => r.score >= 6.6).sort((a, b) => b.score - a.score).slice(0, 8);

    return { n, scoredN: scored.length, avgScore, totalRec, totalApp, approvedRows, pendingRows, highRiskRows: bandRows["High (6.6–10)"],
      hist, bands, statusData, bySector, byRegion, overTime, topExposure, topRisk };
  }, [rows]);

  const breached = useMemo(() => evaluateRules(rows, alertRules).filter((r) => r.breached), [rows, alertRules]);
  const narrative = useMemo(() => (rows.length ? buildNarrative(m) : []), [m, rows.length]);
  const anomalies = useMemo(() => (rows.length ? detectAnomalies(m, rows) : []), [m, rows]);
  const forecast = useMemo(() => forecastSeries(m.overTime, 3), [m.overTime]);
  const ALERTS_TAB = 6;

  // portfolio-wide export
  const exportHeaders = [
    { key: "name", label: "Company" }, { key: "score", label: "ARC Score" }, { key: "band", label: "Risk Band" },
    { key: "recLimit", label: "Recommended Limit" }, { key: "appLimit", label: "Approved Limit" },
    { key: "sector", label: "Sector" }, { key: "country", label: "Country" }, { key: "region", label: "Region" }, { key: "status", label: "Status" },
  ];
  const exportRows = rows.map((r) => ({ ...r, score: r.score ?? "", band: bandLabel(r.score), recLimit: Math.round(r.recLimit || 0), appLimit: Math.round(r.appLimit || 0) }));
  const doExport = (fmt) => {
    setExportEl(null);
    const fname = `CrediArc-Portfolio-${new Date().toISOString().split("T")[0]}`;
    if (fmt === "csv") exportCSV(exportRows, exportHeaders, fname);
    else if (fmt === "xlsx") exportExcel(exportRows, exportHeaders, fname, "Portfolio");
    else exportPDF(exportRows, exportHeaders, fname, "Portfolio Report", `${rows.length} counterparties`);
  };

  const STATUS_COLORS = { Approved: C.green, Pending: C.amber, Rejected: C.red, Declined: C.red };
  const pieColor = (e, i) => [C.ink, C.blue, C.green, C.amber, C.violet, C.red, C.muted][i % 7];

  // Client users retain the 30-day trial experience. Platform administrators
  // can manage BI, reports, alerts and schedules consistently for every tenant.
  if (!isAdmin && !isTrialActive(trial)) {
    return <BIReportingTrial tenantId={tenantId} onTrialChange={setTrial} />;
  }
  if (loading) {
    return <BrandThinkingLoader minHeight={400} />;
  }

  const gridCols = (min) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}, 1fr))`, gap: "14px" });
  const primaryViews = [
    { value: 0, label: "Overview" },
    { value: 1, label: "Risk" },
    { value: 2, label: "Exposure" },
    { value: 3, label: "Markets" },
    { value: 4, label: "Insights" },
  ];
  const workflowViews = [
    { value: 5, label: "Build a report" },
    { value: 6, label: breached.length ? `Alerts (${breached.length})` : "Alerts" },
    { value: 7, label: "Scheduled reports" },
  ];
  const workflowActive = tab >= 5;

  return (
    <Box data-jfrog-bi={isJfrogTenant ? "true" : undefined} sx={{ p: { xs: 2, md: 3 }, background: isJfrogTenant ? "#F5F7FB" : C.ground, minHeight: "100%", fontFamily: "Rubik, sans-serif" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: isJfrogTenant ? "flex-start" : "flex-end", gap: 2, flexWrap: "wrap", mb: isJfrogTenant ? 2.25 : 1 }}>
        <Box>
          <Typography sx={{ fontSize: isJfrogTenant ? { xs: 24, md: 28 } : 24, fontWeight: isJfrogTenant ? 800 : 700, color: C.ink, letterSpacing: isJfrogTenant ? "-.025em" : "-.01em" }}>
            {isJfrogTenant ? "Portfolio workspace" : "BI & Reporting"}
          </Typography>
          <Typography sx={{ fontSize: 14, color: C.muted, mt: "2px" }}>
            {isJfrogTenant ? "See what changed, decide what needs attention, and share the result." : `Real-time visibility into your counterparty portfolio — ${m.n.toLocaleString()} companies.`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {isJfrogTenant && <Button onClick={load} disabled={loading} startIcon={<RefreshRoundedIcon />} variant="text"
            sx={{ textTransform: "none", color: C.slate, fontWeight: 650 }}>Refresh data</Button>}
          <Tooltip title="Take a quick guided tour of every BI & Reporting feature" arrow>
            <Button onClick={() => setTourOpen(true)} startIcon={<PlayCircleOutlineIcon />} variant="outlined"
              sx={{ textTransform: "none", borderColor: C.line, color: C.ink, fontWeight: 600, background: isJfrogTenant ? "#fff" : undefined, "&:hover": { borderColor: C.blue, background: "#fff" } }}>
              {isJfrogTenant ? "Take a tour" : "Walk me through it"}
            </Button>
          </Tooltip>
          <Tooltip title="Get an embeddable snippet of your portfolio summary — aggregate figures only, no login required" arrow>
            <span>
              <Button onClick={getEmbed} disabled={embedBusy} startIcon={<CodeIcon />} variant="outlined"
                sx={{ textTransform: "none", borderColor: C.line, color: C.ink, fontWeight: 600, "&:hover": { borderColor: C.blue, background: "#fff" } }}>Embed</Button>
            </span>
          </Tooltip>
          <Tooltip title="Download the current view as CSV, Excel or PDF" arrow>
            <Button onClick={(e) => setExportEl(e.currentTarget)} startIcon={<DownloadIcon />} variant="outlined"
              sx={{ textTransform: "none", borderColor: isJfrogTenant ? C.ink : C.line, color: isJfrogTenant ? "#fff" : C.ink, background: isJfrogTenant ? C.ink : undefined, fontWeight: 600, "&:hover": { borderColor: C.blue, background: isJfrogTenant ? "#26385e" : "#fff" } }}>Export</Button>
          </Tooltip>
          <Menu anchorEl={exportEl} open={!!exportEl} onClose={() => setExportEl(null)}>
            <MenuItem onClick={() => doExport("csv")}>CSV</MenuItem><MenuItem onClick={() => doExport("xlsx")}>Excel</MenuItem><MenuItem onClick={() => doExport("pdf")}>PDF</MenuItem>
          </Menu>
        </Box>
      </Box>

      {isJfrogTenant && (
        <Box sx={{ mb: 2.25 }}>
          <Typography sx={{ color: C.ink, fontSize: 13.5, fontWeight: 750, mb: 1 }}>Needs your attention</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", lg: "repeat(4,minmax(0,1fr))" }, gap: 1.25 }}>
            <ActionCard eyebrow="High risk" value={m.highRiskRows.length.toLocaleString()} detail={`${fmtUSD(m.highRiskRows.reduce((sum, row) => sum + row.recLimit, 0))} recommended exposure`} tone={C.red} action="Review companies" onClick={() => openDrill("High-risk counterparties (6.6+)", m.highRiskRows)} />
            <ActionCard eyebrow="Pending decisions" value={m.pendingRows.length.toLocaleString()} detail={`${m.approvedRows.length.toLocaleString()} companies already approved`} tone={C.amber} action="Open decision queue" onClick={() => openDrill("Pending counterparties", m.pendingRows)} />
            <ActionCard eyebrow="Active alerts" value={breached.length.toLocaleString()} detail={breached.length ? "Portfolio thresholds need review" : "No portfolio thresholds are currently breached"} tone={breached.length ? C.red : C.green} action={breached.length ? "Review alerts" : "Manage alert rules"} onClick={() => setTab(ALERTS_TAB)} />
            <ActionCard eyebrow="Portfolio coverage" value={`${m.n ? Math.round((m.scoredN / m.n) * 100) : 0}%`} detail={`${m.scoredN.toLocaleString()} of ${m.n.toLocaleString()} companies have an ARC score`} tone={C.blue} action="See risk distribution" onClick={() => setTab(1)} />
          </Box>
        </Box>
      )}

      {/* Live alert banner */}
      {breached.length > 0 && (
        <Box onClick={() => setTab(ALERTS_TAB)} sx={{ display: "flex", alignItems: "center", gap: 1, background: "#FDECEC", border: "1px solid #F3C6C6", borderRadius: "8px", p: "9px 13px", mb: 1.5, cursor: "pointer" }}>
          <NotificationsActiveIcon sx={{ fontSize: 18, color: C.red }} />
          <Typography sx={{ fontSize: 13, color: "#8a2b2b", fontWeight: 600 }}>{breached.length} alert{breached.length > 1 ? "s" : ""} breached:</Typography>
          <Typography sx={{ fontSize: 13, color: "#8a2b2b" }}>{breached.map((b) => `${b.metric.label} (${b.metric.fmt(b.value)})`).join(" · ")}</Typography>
        </Box>
      )}

      {!isJfrogTenant && <Box sx={{ display: "flex", alignItems: "center", gap: 1, background: "#EEF4FF", border: "1px solid #D6E4FF", borderRadius: "8px", p: "8px 12px", mb: 2 }}>
        <InfoOutlinedIcon sx={{ fontSize: 17, color: C.blue }} />
        <Typography sx={{ fontSize: 12.5, color: "#274b8f" }}>
          Views run on your live assessment data. Click any KPI or chart to drill into the underlying companies. A dedicated reporting store, query-layer role scoping, and email/scheduled delivery are on the roadmap.
        </Typography>
      </Box>}

      {!isJfrogTenant ? <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, minHeight: 40,
        "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 14, color: C.muted, minHeight: 40 },
        "& .Mui-selected": { color: `${C.ink} !important` }, "& .MuiTabs-indicator": { background: C.blue } }}>
        <Tab label="Portfolio Health" data-tour="bi-tab-0" /><Tab label="Risk Distribution" data-tour="bi-tab-1" /><Tab label="Limits &amp; Exposure" data-tour="bi-tab-2" /><Tab label="Sectors &amp; Geography" data-tour="bi-tab-3" /><Tab label="Insights" data-tour="bi-tab-4" /><Tab label="Report Builder" data-tour="bi-tab-5" /><Tab label={breached.length ? `Alerts (${breached.length})` : "Alerts"} data-tour="bi-tab-6" /><Tab label="Schedules" data-tour="bi-tab-7" />
      </Tabs> : (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2, p: .6, border: `1px solid ${C.line}`, borderRadius: "12px", background: "#fff", overflowX: "auto" }}>
          <Box role="tablist" aria-label="Portfolio views" sx={{ display: "flex", gap: .35, minWidth: "max-content" }}>
            {primaryViews.map((view) => (
              <Button key={view.value} role="tab" aria-selected={tab === view.value} data-tour={`bi-tab-${view.value}`} onClick={() => setTab(view.value)}
                sx={{ minHeight: 36, px: 1.55, borderRadius: "8px", textTransform: "none", fontSize: 13.5, fontWeight: 700, color: tab === view.value ? C.ink : C.muted, background: tab === view.value ? "#EEF3FC" : "transparent", "&:hover": { background: "#F3F6FB", color: C.ink } }}>
                {view.label}
              </Button>
            ))}
          </Box>
          <Button onClick={(event) => setToolsEl(event.currentTarget)} endIcon={<ExpandMoreRoundedIcon />} variant={workflowActive ? "contained" : "text"}
            sx={{ flex: "0 0 auto", minHeight: 36, borderRadius: "8px", textTransform: "none", fontSize: 13.5, fontWeight: 700, color: workflowActive ? "#fff" : C.slate, background: workflowActive ? C.ink : "transparent", "&:hover": { background: workflowActive ? "#26385e" : "#F3F6FB" } }}>
            {workflowActive ? workflowViews.find((view) => view.value === tab)?.label : "Reports & alerts"}
          </Button>
          <Menu anchorEl={toolsEl} open={!!toolsEl} onClose={() => setToolsEl(null)} PaperProps={{ sx: { mt: .5, minWidth: 210, borderRadius: "10px" } }}>
            {workflowViews.map((view) => <MenuItem key={view.value} selected={tab === view.value} onClick={() => { setTab(view.value); setToolsEl(null); }} sx={{ fontSize: 13.5, py: 1.1 }}>{view.label}</MenuItem>)}
          </Menu>
        </Box>
      )}

      {/* PORTFOLIO HEALTH */}
      {tab === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Box sx={gridCols("180px")}>
            <KpiCard label="Counterparties" value={m.n.toLocaleString()} sub={`${m.scoredN} scored`} onClick={() => openDrill("All counterparties", rows)} />
            <KpiCard label="Recommended exposure" value={fmtUSD(m.totalRec)} sub="Sum of recommended limits" onClick={() => openDrill("All counterparties", rows)} />
            <KpiCard label="Approved exposure" value={fmtUSD(m.totalApp)} sub={`${Math.round((m.totalApp / (m.totalRec || 1)) * 100)}% of recommended`} onClick={() => openDrill("Approved counterparties", m.approvedRows)} />
            <KpiCard label="Avg ARC score" value={m.avgScore != null ? m.avgScore.toFixed(1) : "—"} sub="1 = best · 10 = worst" accent={bandColor(m.avgScore)} onClick={() => openDrill("High-risk counterparties (6.6+)", m.highRiskRows)} />
            <KpiCard label="Pending review" value={m.pendingRows.length.toLocaleString()} sub={`${m.approvedRows.length} approved`} accent={C.amber} onClick={() => openDrill("Pending counterparties", m.pendingRows)} />
          </Box>
          <Box sx={gridCols("340px")}>
            <ChartCard title="New counterparties over time" subtitle="Companies added per month (last 12)">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={m.overTime} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid {...gridProps} /><XAxis dataKey="month" tick={axis} /><YAxis tick={axis} allowDecimals={false} />
                  <RTooltip /><Line type="monotone" dataKey="count" stroke={C.blue} strokeWidth={2.5} dot={{ r: 2.5 }} name="Added" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Approval status" subtitle="Click a segment to drill in">
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie data={m.statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={2}
                    cursor="pointer" onClick={(d) => openDrill(`${d.name} counterparties`, d._rows)}>
                    {m.statusData.map((e, i) => <Cell key={i} fill={STATUS_COLORS[e.name] || C.muted} />)}
                  </Pie>
                  <RTooltip /><Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>
          <ChartCard title="Risk band mix" subtitle="Click a band to drill in">
            <ResponsiveContainer width="100%" height={120}>
              <BarChart layout="vertical" data={[{ name: "Portfolio", ...Object.fromEntries(m.bands.map((b) => [b.key, b.count])) }]} margin={{ left: 0, right: 12 }} stackOffset="expand">
                <XAxis type="number" hide /><YAxis type="category" dataKey="name" hide />
                <RTooltip formatter={(v, n) => [`${v} companies`, n]} />
                {m.bands.map((b) => <Bar key={b.key} dataKey={b.key} stackId="a" fill={b.color} cursor="pointer" onClick={() => openDrill(`${b.key} risk band`, b._rows)} />)}
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Box>
      )}

      {/* RISK DISTRIBUTION */}
      {tab === 1 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <ChartCard title="ARC score distribution" subtitle="Click a bar to see those companies (1 = best, 10 = worst)">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={m.hist} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid {...gridProps} /><XAxis dataKey="bucket" tick={axis} /><YAxis tick={axis} allowDecimals={false} />
                <RTooltip formatter={(v) => [`${v} companies`, "Count"]} labelFormatter={(l) => `ARC ${l}`} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => openDrill(`ARC score ${d.bucket}`, d._rows)}>
                  {m.hist.map((h, i) => <Cell key={i} fill={bandColor(i + 1)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <Box sx={gridCols("340px")}>
            <ChartCard title="Average risk by sector" subtitle="Mean ARC score per sector · click to drill">
              <ResponsiveContainer width="100%" height={Math.max(200, m.bySector.length * 30)}>
                <BarChart layout="vertical" data={m.bySector.filter((s) => s.avgScore != null)} margin={{ left: 8, right: 20 }}>
                  <CartesianGrid {...gridProps} horizontal={false} /><XAxis type="number" domain={[0, 10]} tick={axis} />
                  <YAxis type="category" dataKey="sector" tick={{ ...axis, fontSize: 10.5 }} width={120} />
                  <RTooltip formatter={(v) => [v, "Avg ARC"]} />
                  <Bar dataKey="avgScore" radius={[0, 4, 4, 0]} cursor="pointer" onClick={(d) => openDrill(`${d.sector}`, d._rows)}>{m.bySector.map((s, i) => <Cell key={i} fill={bandColor(s.avgScore)} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Highest-risk counterparties" subtitle="ARC score 6.6+ (top 8)">
              <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {m.topRisk.length === 0 && <Typography sx={{ fontSize: 13, color: C.muted, py: 3, textAlign: "center" }}>No high-risk counterparties.</Typography>}
                {m.topRisk.map((r, i) => (
                  <Box key={i} onClick={() => openCompany(r.companyId)}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, borderBottom: `1px solid ${C.line}`, py: "6px", px: "6px", mx: "-6px", borderRadius: "6px", cursor: r.companyId ? "pointer" : "default", "&:hover": r.companyId ? { background: "#F7F9FE" } : {} }}>
                    <Typography sx={{ fontSize: 13, color: C.blue, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</Typography>
                    <Chip label={r.score.toFixed(1)} size="small" sx={{ height: 20, fontWeight: 700, fontSize: 11.5, background: "#FDECEC", color: C.red }} />
                  </Box>
                ))}
              </Box>
            </ChartCard>
          </Box>
        </Box>
      )}

      {/* LIMITS & EXPOSURE */}
      {tab === 2 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Box sx={gridCols("200px")}>
            <KpiCard label="Recommended exposure" value={fmtUSD(m.totalRec)} sub={fmtFull(m.totalRec)} onClick={() => openDrill("All counterparties", rows)} />
            <KpiCard label="Approved exposure" value={fmtUSD(m.totalApp)} sub={fmtFull(m.totalApp)} accent={C.green} onClick={() => openDrill("Approved counterparties", m.approvedRows)} />
            <KpiCard label="Avg recommended limit" value={fmtUSD(m.n ? m.totalRec / m.n : 0)} sub="Per counterparty" />
          </Box>
          <ChartCard title="Recommended exposure by sector" subtitle="Sum of recommended limits · click to drill (top 10)">
            <ResponsiveContainer width="100%" height={Math.max(220, m.bySector.length * 34)}>
              <BarChart layout="vertical" data={m.bySector} margin={{ left: 8, right: 24 }}>
                <CartesianGrid {...gridProps} horizontal={false} /><XAxis type="number" tick={axis} tickFormatter={fmtUSD} />
                <YAxis type="category" dataKey="sector" tick={{ ...axis, fontSize: 10.5 }} width={130} />
                <RTooltip formatter={(v) => [fmtFull(v), "Exposure"]} />
                <Bar dataKey="exposure" fill={C.blue} radius={[0, 4, 4, 0]} cursor="pointer" onClick={(d) => openDrill(`${d.sector}`, d._rows)} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Top counterparties by recommended limit" subtitle="Largest single exposures">
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {m.topExposure.map((r, i) => (
                <Box key={i} onClick={() => openCompany(r.companyId)}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, borderBottom: `1px solid ${C.line}`, py: "7px", px: "6px", mx: "-6px", borderRadius: "6px", cursor: r.companyId ? "pointer" : "default", "&:hover": r.companyId ? { background: "#F7F9FE" } : {} }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: bandColor(r.score), flex: "0 0 auto" }} />
                    <Typography sx={{ fontSize: 13, color: C.blue, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: C.muted, flex: "0 0 auto" }}>· {r.sector}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.ink, fontVariantNumeric: "tabular-nums", flex: "0 0 auto" }}>{fmtFull(r.recLimit)}</Typography>
                </Box>
              ))}
            </Box>
          </ChartCard>
        </Box>
      )}

      {/* SECTORS & GEOGRAPHY */}
      {tab === 3 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Box sx={gridCols("340px")}>
            <ChartCard title="Counterparties by region" subtitle="Click a bar to drill in">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={m.byRegion} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid {...gridProps} /><XAxis dataKey="region" tick={{ ...axis, fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} /><YAxis tick={axis} allowDecimals={false} />
                  <RTooltip /><Bar dataKey="count" fill={C.violet} radius={[4, 4, 0, 0]} name="Companies" cursor="pointer" onClick={(d) => openDrill(`${d.region}`, d._rows)} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Exposure by region" subtitle="Recommended limit sum per region">
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie data={m.byRegion.map((r) => ({ name: r.region, value: r.exposure, _rows: r._rows }))} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={2}
                    cursor="pointer" onClick={(d) => openDrill(`${d.name}`, d._rows)}>
                    {m.byRegion.map((e, i) => <Cell key={i} fill={pieColor(e, i)} />)}
                  </Pie>
                  <RTooltip formatter={(v) => [fmtFull(v), "Exposure"]} /><Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>
          <ChartCard title="Sector breakdown" subtitle="Click a row to drill into that sector (top 10 by exposure)">
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: 480 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1.6fr .7fr .9fr 1fr", px: 1, py: "6px", background: C.ground, borderRadius: "6px" }}>
                  {["Sector", "Companies", "Avg ARC", "Exposure"].map((h) => <Typography key={h} sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: C.muted }}>{h}</Typography>)}
                </Box>
                {m.bySector.map((s, i) => (
                  <Box key={i} onClick={() => openDrill(`${s.sector}`, s._rows)} sx={{ display: "grid", gridTemplateColumns: "1.6fr .7fr .9fr 1fr", px: 1, py: "8px", borderBottom: `1px solid ${C.line}`, alignItems: "center", cursor: "pointer", "&:hover": { background: "#F7F9FE" } }}>
                    <Typography sx={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{s.sector}</Typography>
                    <Typography sx={{ fontSize: 13, color: C.slate }}>{s.count}</Typography>
                    <Box><Chip label={s.avgScore != null ? s.avgScore : "—"} size="small" sx={{ height: 20, fontWeight: 700, fontSize: 11.5, background: `${bandColor(s.avgScore)}1A`, color: bandColor(s.avgScore) }} /></Box>
                    <Typography sx={{ fontSize: 13, color: C.slate, fontVariantNumeric: "tabular-nums" }}>{fmtFull(s.exposure)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </ChartCard>
        </Box>
      )}

      {/* INSIGHTS (narrative summary · anomaly detection · forecast) */}
      {tab === 4 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <ChartCard title="Portfolio summary" subtitle="Auto-generated from your live data">
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {narrative.map((s, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                  <AutoAwesomeIcon sx={{ fontSize: 16, color: C.blue, mt: "3px", flex: "0 0 auto" }} />
                  <Typography sx={{ fontSize: 14, color: C.slate, lineHeight: 1.6 }}>{s}</Typography>
                </Box>
              ))}
            </Box>
          </ChartCard>

          <ChartCard title="Anomaly detection" subtitle="Statistically or materially unusual signals flagged automatically">
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {anomalies.length === 0 && (
                <Typography sx={{ fontSize: 13.5, color: C.green, py: 1 }}>No anomalies detected — the portfolio looks within normal ranges.</Typography>
              )}
              {anomalies.map((a, i) => {
                const col = a.severity === "high" ? C.red : C.amber;
                return (
                  <Box key={i} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", border: `1px solid ${C.line}`, borderLeft: `4px solid ${col}`, borderRadius: "9px", p: "11px 14px" }}>
                    <WarningAmberIcon sx={{ fontSize: 18, color: col, mt: "1px", flex: "0 0 auto" }} />
                    <Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.ink }}>{a.title}
                        <Chip label={a.severity} size="small" sx={{ ml: 1, height: 17, fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", background: `${col}1A`, color: col }} /></Typography>
                      <Typography sx={{ fontSize: 13, color: C.slate, mt: "2px" }}>{a.detail}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </ChartCard>

          <ChartCard title="Onboarding forecast" subtitle="New counterparties per month with a 3-month trend projection">
            {forecast.hasForecast ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={forecast.data} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid {...gridProps} /><XAxis dataKey="month" tick={axis} /><YAxis tick={axis} allowDecimals={false} />
                  <RTooltip /><Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="count" stroke={C.blue} strokeWidth={2.5} dot={{ r: 2.5 }} name="Actual" connectNulls={false} />
                  <Line type="monotone" dataKey="forecast" stroke={C.violet} strokeWidth={2} strokeDasharray="6 5" dot={{ r: 2.5 }} name="Forecast" connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: 13.5, color: C.muted, py: 2 }}>Not enough history yet to project a trend.</Typography>
            )}
          </ChartCard>
        </Box>
      )}

      {/* REPORT BUILDER */}
      {tab === 5 && <ReportBuilder tenantId={tenantId} rows={rows} onDrill={(title, subset) => openDrill(title, subset)} />}

      {/* ALERTS */}
      {tab === 6 && <AlertsPanel rows={rows} onRulesChange={setAlertRules} tenantId={tenantId} />}

      {/* SCHEDULED EXPORTS */}
      {tab === 7 && <SchedulesPanel tenantId={tenantId} />}

      <DrillDownDialog open={drill.open} title={drill.title} rows={drill.rows} onClose={() => setDrill((d) => ({ ...d, open: false }))} onOpenCompany={(cid) => { setDrill((d) => ({ ...d, open: false })); openCompany(cid); }} />

      <Dialog open={!!embedCode} onClose={() => setEmbedCode(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: C.ink }}>Embed this summary</Typography>
            <Typography sx={{ fontSize: 12.5, color: C.muted }}>Paste this snippet into any page or portal — no login required.</Typography>
          </Box>
          <IconButton size="small" onClick={() => setEmbedCode(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField value={embedCode || ""} multiline minRows={3} fullWidth InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }} onFocus={(e) => e.target.select()} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
            <Button startIcon={<ContentCopyIcon />} variant="contained" onClick={() => { navigator.clipboard?.writeText(embedCode || ""); toast.success("Embed code copied."); }}
              sx={{ textTransform: "none", background: C.blue, "&:hover": { background: "#2455c0" } }}>Copy code</Button>
          </Box>
          <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 1.5 }}>The widget shows aggregate figures only (no company-level data) and refreshes on load. The link expires in 90 days.</Typography>
        </DialogContent>
      </Dialog>

      <SpotlightTour open={tourOpen} steps={tourSteps} onClose={() => setTourOpen(false)} />
    </Box>
  );
};

export default BIReporting;
